import axios from "axios";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import store from "redux/store";

// api
import api from "api/api";

/**
 * A custom React hook that fetches and manages demand data for a given route.
 * It uses Redux state for demand data and local storage for caching.
 *
 * @param {string} origin - The id of the origin airport for the demand data.
 * @param {string} destination - The id of the destination airport for the demand data.
 * @returns {[boolean, (number[])?]} - A tuple containing a loading state and the demand data.
 *  - The loading state is a boolean indicating whether the demand data is currently being fetched.
 *  - The demand data is an array of numbers representing the demand for economy, premium, business, and first class flights.
 *    If the demand data is not available or hasn't been loaded yet, it will be null.
 */
function useDemand(origin, destination) {
	const demands = useSelector(state => state.demand);
	const [demand, setDemand] = useState(null);
	const [loading, startTransition] = useTransition();
	const controller = useRef(new AbortController());

	useEffect(() => {
		if (!origin || !destination) return;

		startTransition(() => {
			const d = getCurrentDemand(origin, destination);

			if (!d) {
				fetchDefaultDemand(origin, destination, controller.current).then(setDemand);
				return () => {
					controller.current.abort();
					controller.current = new AbortController();
				};
			}

			setDemand([d.demand_economy, d.demand_premium, d.demand_business, d.demand_first]);
		});
	}, [origin, demands, destination]);

	return [loading || (demand == null && origin && destination), demand];
}

/**
 * A custom React hook that fetches and manages default demand data for a given route.
 * It uses local storage for caching.
 *
 * @param {string} origin - The id of the origin airport for the demand data.
 * @param {string} destination - The id of the destination airport for the demand data.
 * @returns {number[]} - An array containing the default demand for economy, premium economy, business, and first class flights.
 *  - If the demand data is not available or hasn't been loaded yet, it will be null.
 */
function useDefaultDemand(origin, destination) {
	const [demand, setDemand] = useState(null);
	const controller = useRef(new AbortController());

	useEffect(() => {
		if (!origin || !destination) return;
		fetchDefaultDemand(origin, destination, controller.current).then(setDemand);

		const ref = controller.current;
		return () => {
			ref.abort();
			controller.current = new AbortController();
		};
	}, [origin, destination]);

	return demand;
}

function useScheduleDemandRefresh() {
	const dispatch = useDispatch();
	const refresh = useCallback(async () => {
		try {
			console.log("Refreshing demand...");
			const { data } = await axios.get(`${api}/group/demand`);
			dispatch({ type: "SET_DEMAND", payload: data });
			console.log("Refresh finished");
		} catch (error) {
			throw error;
		}
	}, [dispatch]);

	useEffect(() => {
		const now = DateTime.now().setZone("Europe/Warsaw");
		const midnight = now.endOf("day");
		const duration = midnight.diff(now);
		const timeLeft = duration.as("milliseconds") + 2000; // Add 2 seconds to be sure that cron started running
		const timeout = setTimeout(refresh, timeLeft);

		return () => clearTimeout(timeout);
	}, [refresh]);
}

function getCurrentDemand(origin, destination) {
	return store
		.getState()
		.demand.find(
			d =>
				(d.destination === destination && d.origin === origin) ||
				(d.destination === origin && d.origin === destination)
		);
}

async function fetchDefaultDemand(origin, destination, controller = new AbortController()) {
	const fromCache = localStorage.getItem(`${origin}-${destination}`) || localStorage.getItem(`${destination}-${origin}`);
	if (fromCache) return fromCache.split(",");

	try {
		const { data } = await axios.get(`${api}/demand?origin=${origin}&destination=${destination}`, {
			signal: controller.signal
		});
		localStorage.setItem(`${origin}-${destination}`, data);
		return data;
	} catch (e) {
		console.error(e);
		return null;
	}
}

export { useDemand, getCurrentDemand, useScheduleDemandRefresh, useDefaultDemand };
export default useDemand;
